import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  createFinancialAssetCategory,
  fetchFinancialAssetTypes,
  fetchBusinessUnitsByDomain,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function FinancialAssetCategoryCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [financialAssetTypes, setFinancialAssetTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [form, setForm] = useState({
    financialAssetCategoryName: "",
    financialAssetCategoryDescription: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [types, units] = await Promise.all([
          fetchFinancialAssetTypes(businessDomainId),
          fetchBusinessUnitsByDomain(businessDomainId),
        ]);
        setFinancialAssetTypes(Array.isArray(types) ? types : []);
        const unit = Array.isArray(units) ? units[0] : null;
        setBusinessUnitId(unit?.businessUnitId || null);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypesChange = (e) => {
    const values = e.target.value;
    setSelectedTypes(typeof values === "string" ? [] : values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createFinancialAssetCategory({
        companyId: COMPANY_ID,
        businessUnitId,
        financialAssetCategoryName: form.financialAssetCategoryName,
        financialAssetCategoryDescription: form.financialAssetCategoryDescription || null,
        associatedFinancialAssetTypes: selectedTypes.map((ft) => ({
          financialAssetTypeId: ft.financialAssetTypeId,
          financialAssetTypeName: ft.financialAssetTypeName,
        })),
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/financial-asset-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("financialAssetCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required
              label={t("financialAssetCategory.name")}
              name="financialAssetCategoryName"
              value={form.financialAssetCategoryName}
              onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialAssetCategory.financialAssetTypes")}</InputLabel>
              <Select
                multiple
                value={selectedTypes}
                onChange={handleTypesChange}
                input={<OutlinedInput label={t("financialAssetCategory.financialAssetTypes")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v.financialAssetTypeId} label={v.financialAssetTypeName} size="small" />
                    ))}
                  </Box>
                )}
              >
                {financialAssetTypes.map((ft) => (
                  <MenuItem key={ft.financialAssetTypeId} value={ft}>
                    {ft.financialAssetTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3}
              label={t("common.description")}
              name="financialAssetCategoryDescription"
              value={form.financialAssetCategoryDescription}
              onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/financial-asset-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
